/**
 * Karbon360° v2 — Account.
 *
 * Your own profile, plan and password. Built entirely from the v2 primitives.
 *
 * Role and plan are shown but NOT editable here — the API refuses to write
 * role/subscription_plan/status/email from this endpoint, since self-service
 * edits to those would be privilege escalation.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ErrorBoundary from '../components/ErrorBoundary';
import { Shell } from '../components/Shell';
import { X } from '../components/v2/Icons';
import { clearSession } from '../components/v2/session';
import {
  PageHeader, Card, Button, Field, Input, Select, Badge, Callout,
  DList, Skeleton, useToast,
} from '../components/v2/ui';
import { useT } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PLAN_LABEL = { basic: 'Basic', advanced: 'Advanced', corporate: 'Corporate' };
const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', user: 'Subscriber' };

const EDITABLE = [
  { key: 'full_name', label: 'Full name' },
  { key: 'phone', label: 'Phone' },
  { key: 'occupation', label: 'Role / job title' },
  { key: 'company', label: 'Company' },
  { key: 'country', label: 'Country' },
];

function Account() {
  const { t } = useT();
  const { toast } = useToast();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/api/auth/me`, auth())
      .then((r) => {
        if (!alive) return;
        setMe(r.data);
        setForm(EDITABLE.reduce((a, f) => ({ ...a, [f.key]: r.data[f.key] || '' }), {}));
      })
      .catch((e) => { if (alive) setLoadError(e.response?.data?.message || 'Could not load your account.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API_URL}/api/profile`, form, auth());
      setMe(data);
      clearSession(); // so the topbar picks up a changed name on next load
      toast('Account details saved');
    } catch (err) {
      toast(err.response?.data?.message || 'Could not save your details', { tone: 'err' });
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError('');

    if (pw.newPassword !== pw.confirm) {
      setPwError('The new password and its confirmation do not match.');
      return;
    }
    if (pw.newPassword.length < 8) {
      setPwError('The new password must be at least 8 characters.');
      return;
    }

    setPwSaving(true);
    try {
      await axios.post(`${API_URL}/api/profile/password`,
        { currentPassword: pw.currentPassword, newPassword: pw.newPassword }, auth());
      setPw({ currentPassword: '', newPassword: '', confirm: '' });
      toast('Password updated');
    } catch (err) {
      setPwError(err.response?.data?.message || 'Could not change your password.');
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="k2-wrap" style={{ maxWidth: 900 }}>
        <PageHeader title={t('acc.title')} desc={t('acc.desc')} />
        <Card><div style={{ display: 'grid', gap: 10 }}>
          <Skeleton h={30} w="45%" /><Skeleton h={20} w="70%" /><Skeleton h={20} w="60%" />
        </div></Card>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="k2-wrap" style={{ maxWidth: 900 }}>
        <PageHeader title={t('acc.title')} />
        <Callout tone="err">{loadError}</Callout>
      </div>
    );
  }

  const role = me?.role || 'user';
  const plan = me?.subscription_plan || '';

  return (
    <div className="k2-wrap" style={{ maxWidth: 900 }}>
      <PageHeader title={t('acc.title')} desc={t('acc.desc')} />

      <div className="k2-gcol">
        <Card title={t('acc.identity')} sub={t('acc.identitySub')}>
          <DList rows={[
            ['Username', me?.username || '—'],
            ['Email', me?.email || '—'],
            ['Access level', (
              <span key="role" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <Badge tone={role === 'owner' ? 'dark' : role === 'admin' ? 'mint' : 'out'}>
                  {ROLE_LABEL[role] || role}
                </Badge>
                {role === 'user' && <span style={{ fontSize: 12, color: 'var(--n500)' }}>standard account</span>}
              </span>
            )],
            ['Plan', PLAN_LABEL[plan] || plan || '—'],
            ['Member since', me?.created_at ? new Date(me.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'],
          ]} />
        </Card>

        <Card
          title={t('acc.details')}
          sub={t('acc.detailsSub')}
          footer={
            <Button variant="mint" icon={X.Check} onClick={saveProfile} disabled={saving} style={{ marginLeft: 'auto' }}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          }
        >
          <form onSubmit={saveProfile}>
            <div className="k2-g2">
              {EDITABLE.map((f) => (
                <Field key={f.key} label={f.label}>
                  <Input
                    value={form[f.key] ?? ''}
                    onChange={(e) => setForm((v) => ({ ...v, [f.key]: e.target.value }))}
                    placeholder={f.label}
                  />
                </Field>
              ))}
              <Field label={t('acc.companySize')}>
                <Select
                  value={form.company_size ?? ''}
                  onChange={(e) => setForm((v) => ({ ...v, company_size: e.target.value }))}
                >
                  <option value="">Not specified</option>
                  <option value="1-10">1–10</option>
                  <option value="11-50">11–50</option>
                  <option value="51-250">51–250</option>
                  <option value="250+">250+</option>
                </Select>
              </Field>
            </div>
            {/* Submitting via the footer button; this keeps Enter working too. */}
            <button type="submit" hidden aria-hidden="true" />
          </form>
        </Card>

        <Card
          title={t('acc.password')}
          sub="You must confirm your current password — that stops a borrowed session from locking you out."
          footer={
            <Button
              variant="pri"
              icon={X.Shield}
              onClick={changePassword}
              disabled={pwSaving || !pw.currentPassword || !pw.newPassword || !pw.confirm}
              style={{ marginLeft: 'auto' }}
            >
              {pwSaving ? 'Updating…' : 'Update password'}
            </Button>
          }
        >
          <form onSubmit={changePassword}>
            {pwError && <div style={{ marginBottom: 14 }}><Callout tone="err">{pwError}</Callout></div>}
            <Field label={t('acc.currentPassword')} required>
              <Input
                type="password"
                autoComplete="current-password"
                value={pw.currentPassword}
                onChange={(e) => setPw((v) => ({ ...v, currentPassword: e.target.value }))}
              />
            </Field>
            <div className="k2-g2">
              <Field label={t('acc.newPassword')} required hint="At least 8 characters.">
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={pw.newPassword}
                  onChange={(e) => setPw((v) => ({ ...v, newPassword: e.target.value }))}
                />
              </Field>
              <Field
                label={t('acc.confirmPassword')}
                required
                error={pw.confirm && pw.newPassword !== pw.confirm ? 'Does not match.' : ''}
              >
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={pw.confirm}
                  onChange={(e) => setPw((v) => ({ ...v, confirm: e.target.value }))}
                />
              </Field>
            </div>
            <button type="submit" hidden aria-hidden="true" />
          </form>
        </Card>
      </div>
    </div>
  );
}

export default function AccountPage() {
  return (
    <ErrorBoundary>
      <Shell>
        <Account />
      </Shell>
    </ErrorBoundary>
  );
}
